import { Box, Button, FormControlLabel, Menu, MenuItem, Switch } from '@mui/material';
import { useState } from 'react';
import { useCurrentOrg, useOrganization } from '~/providers/GraphqlProvider';
import { Permission, useHasPermission } from '~/hooks/permissions';
import useDefaultServiceLines from '~/hooks/useDefaultServiceLines';
import { FilterState } from '~/views/command/filters';
import { useOrgConfig } from '~/providers/OrgConfigProvider';
import LabeledCheckboxSelect from '../common/forms/LabeledCheckboxSelect';

interface FilterProps {
  filters: FilterState;
  onChange: (state: FilterState) => void;
  viewMode: "provider" | "patient";
}

export const filtersStorageKey = (orgId: number) => `quickFilters:${orgId}`;

export const useDefaultFilters = (): FilterState => {
  const serviceLineIds = useDefaultServiceLines();
  const { serviceLines, loading } = useOrgConfig();
  const orgId = useOrganization().organizationId;

  const filters: FilterState = {
    serviceLineIds,
    showMyVisits: false,
    showCanceledVisits: false,
    showAllProviders: false,
    showCanceledShifts: false,
    showAllPatients: true,
    ...JSON.parse((orgId && localStorage[filtersStorageKey(orgId)]) || "{}"),
  }

  const storedIds = Array.isArray(filters.serviceLineIds) ? filters.serviceLineIds : [];
  filters.serviceLineIds = loading ? storedIds : storedIds.filter(id => serviceLines[id]);

  return filters;
}

const FilterMenu: React.FC<FilterProps> = ({
  filters,
  onChange,
  viewMode,
}) => {
  const isPatientView = viewMode === "patient";
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const providerId = useCurrentOrg()?.providerId;
  const { organizationId } = useOrganization();

  const { serviceLines } = useOrgConfig();

  const toggle = (key: keyof FilterState) => () => setState({
    ...filters,
    [key]: !filters[key],
  })

  const setState = (newFilters: FilterState) => {
    if (organizationId) {
      localStorage[filtersStorageKey(organizationId)] = JSON.stringify(newFilters)
    }
    onChange(newFilters);
  }

  // TODO: When releasing calendar blocks, remove this permission checks.
  // The "& blocks" label should be visible to everyone, regardless of their permissions.
  // For now it's just a way to hide the label to be dev-only (same as the "Add Blocks" button)
  const manageBlocksPerm = useHasPermission(Permission.manageCalendarBlocks);

  return <>
    <Button
      variant="contained"
      size="small"
      onClick={(e) => setAnchorEl(e.currentTarget)}
      sx={{
        height: "28px",
        minWidth: "60px",
        fontSize: "0.75rem",
      }}
    >
      Filters
    </Button>
    <Menu
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={() => setAnchorEl(null)}
    >
      <MenuItem>
        {providerId && <FormControlLabel
          control={<Switch size="small" checked={filters.showMyVisits} onChange={toggle("showMyVisits")} />}
          label="Only my visits"
        />}
      </MenuItem>
      {!isPatientView && (
        <MenuItem>
          <FormControlLabel
            control={<Switch size="small" checked={filters.showAllProviders} onChange={toggle("showAllProviders")} />}
            label="Show all providers"
          />
        </MenuItem>
      )}
      {isPatientView && (
        <MenuItem>
          <FormControlLabel
            control={<Switch size="small" checked={filters.showAllPatients} onChange={toggle("showAllPatients")} />}
            label="Show all patients"
          />
        </MenuItem>
      )}
      {!isPatientView && (
        <MenuItem>
          <FormControlLabel
            control={<Switch size="small" checked={filters.showCanceledShifts} onChange={toggle("showCanceledShifts")} />}
            label={manageBlocksPerm ? "Show canceled shifts & blocks" : "Show canceled shifts"}
          />
        </MenuItem>
      )}
      <MenuItem>
        <FormControlLabel
          control={<Switch size="small" checked={filters.showCanceledVisits} onChange={toggle("showCanceledVisits")} />}
          label="Show canceled visits"
        />
      </MenuItem>
      {Object.keys(serviceLines).length > 1 && <Box sx={{ p: 2 }}>
        <LabeledCheckboxSelect
          label="Service Lines"
          fullWidth
          sx={{ width: 325 }}
          size="small"
          value={filters.serviceLineIds}
          onChange={(serviceLineIds) => setState({ ...filters, serviceLineIds})}
          options={Object.values(serviceLines).filter(sl => sl !== undefined).map(sl => ({ key: sl.id, label: sl.name }))}
        />
      </Box>}
    </Menu>
  </>
}

export default FilterMenu;
